import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { TextField } from "../../../../ui/components/TextField";
import { Table } from "../../../../ui/components/Table";
import { Button } from "../../../../ui/components/Button";
import { Loader } from "../../../../ui/components/Loader";
import { Badge } from "../../../../ui/components/Badge";
import { IconButton } from "../../../../ui/components/IconButton";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import {
  FeatherAlertTriangle,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherEdit,
  FeatherPlus,
  FeatherSearch,
  FeatherTrash,
  FeatherUpload,
  FeatherX,
} from "@subframe/core";
import {
  useToast,
  ReviewRequestModal,
} from "../../../../shared/presentation/components";
import { getAllCustomers } from "../../../../services/customerApi";
import { usePermissions } from "../../../../shared/presentation/hooks";
import {
  ACTION_CODES,
  MODULE_CODES,
} from "../../../../shared/constants/permissions";
import { deleteZohoContact } from "../../../../shared/data/api/contactsApi";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { BulkUploadModal } from "./BulkUploadModal";

type CustomersListItem = {
  id?: number;
  customer_id?: string;
  zoho_customerid?: string;
  customer_name?: string;
  country?: string;
  state?: string;
  gstin_vat_tax_id?: string | null;
  is_under_review?: number;
};

type CustomersResponse = {
  success: boolean;
  data: CustomersListItem[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
};

const DEFAULT_LIMIT = 20;
type ListLoadingReason = "initial" | "pagination" | "search" | null;

export const CustomersMastersTab: React.FC = () => {
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const { getPermissionInfo, loading: permissionsLoading } = usePermissions();

  const [items, setItems] = useState<CustomersListItem[]>([]);
  const [pagination, setPagination] = useState<
    CustomersResponse["pagination"] | null
  >(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loadingReason, setLoadingReason] =
    useState<ListLoadingReason>("initial");

  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<{
    entityId: number;
    zohoContactId: string;
    name: string;
  } | null>(null);

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<{
    entityId: number;
    zohoContactId: string;
    name: string;
  } | null>(null);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingStartedRef = useRef(false);

  const offset = useMemo(() => (page - 1) * DEFAULT_LIMIT, [page]);
  const totalPages = useMemo(() => {
    if (!pagination) return 1;
    return Math.max(
      1,
      Math.ceil(pagination.total / (pagination.limit || DEFAULT_LIMIT)),
    );
  }, [pagination]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = (await getAllCustomers({
        limit: DEFAULT_LIMIT,
        offset,
        search: search.trim() ? search.trim() : undefined,
        status: "live",
      })) as unknown as CustomersResponse;

      if (!res.success) throw new Error("Failed to fetch customers");

      setItems(Array.isArray(res.data) ? res.data : []);
      setPagination(res.pagination ?? null);
    } catch (err: unknown) {
      showError(
        err instanceof Error ? err.message : "Failed to fetch customers",
      );
      setItems([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [offset, search, showError]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (loading) {
      loadingStartedRef.current = true;
      return;
    }

    if (loadingStartedRef.current) {
      loadingStartedRef.current = false;
      setLoadingReason(null);
    }
  }, [loading]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        if (value === search) return;

        setLoadingReason("search");
        setPage(1);
        setSearch(value);
      }, 300);
    },
    [search],
  );

  const handlePageChange = useCallback((nextPage: number) => {
    setLoadingReason("pagination");
    setPage(nextPage);
  }, []);

  const canDelete = getPermissionInfo(
    MODULE_CODES.CUSTOMER,
    ACTION_CODES.DELETE,
  ).granted;

  const bulkUploadPerm = getPermissionInfo(
    MODULE_CODES.CUSTOMER,
    ACTION_CODES.BULK_UPLOAD,
  );

  const handleDeleteClick = useCallback(
    async (c: CustomersListItem) => {
      const name = (c.customer_name ?? "").trim() || "this customer";
      const zohoId = (c.zoho_customerid ?? "").trim();
      const localId = Number(c.customer_id ?? c.id ?? 0);
      if (!zohoId || !localId) {
        showError("Missing customer or Zoho ID");
        return;
      }
      if (!canDelete) {
        showError("You do not have permission to delete customers");
        return;
      }
      setCustomerToDelete({
        entityId: localId,
        zohoContactId: zohoId,
        name: name || "Customer",
      });
      setDeleteDialogOpen(true);
    },
    [canDelete, showError],
  );

  const deleteRequiresReview = getPermissionInfo(
    MODULE_CODES.CUSTOMER,
    ACTION_CODES.DELETE,
  ).requiresReview;

  const handleDeleteConfirm = useCallback(async () => {
    if (!customerToDelete) return;

    const { granted, requiresReview } = getPermissionInfo(
      MODULE_CODES.CUSTOMER,
      ACTION_CODES.DELETE,
    );
    if (!granted) {
      showError("You do not have permission to delete customers");
      return;
    }

    if (requiresReview) {
      setDeleteDialogOpen(false);
      setReviewTarget(customerToDelete);
      setReviewModalOpen(true);
      return;
    }

    setIsDeleting(true);
    try {
      await deleteZohoContact({
        zohoContactId: customerToDelete.zohoContactId,
        entityType: "customer",
        entityId: customerToDelete.entityId,
      });
      showSuccess("Customer deleted");
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      await fetchCustomers();
    } catch (e: unknown) {
      const err = e as Error & { code?: string };
      const msg = e instanceof Error ? e.message : "Failed to delete customer";
      if (err.code === "REVIEW_REQUIRED") {
        setDeleteDialogOpen(false);
        setReviewTarget(customerToDelete);
        setReviewModalOpen(true);
        return;
      }
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  }, [
    customerToDelete,
    fetchCustomers,
    getPermissionInfo,
    showError,
    showSuccess,
  ]);

  const showTableSkeleton =
    loadingReason === "initial" ||
    loadingReason === "pagination" ||
    (loading && !pagination);
  const showInlineLoading =
    !showTableSkeleton && (loadingReason === "search" || loading);

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full items-center justify-between gap-4">
        <TextField
          className="h-auto w-96 flex-none"
          variant="filled"
          icon={<FeatherSearch />}
        >
          <TextField.Input
            placeholder="Search customers..."
            value={searchInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSearchChange(e.target.value)
            }
          />
        </TextField>
        <div className="flex items-center gap-4">
          {pagination && (
            <span className="text-body font-body text-subtext-color whitespace-nowrap">
              {pagination.total} customer{pagination.total !== 1 ? "s" : ""} in
              total
            </span>
          )}
          {bulkUploadPerm.granted ? (
            <Button
              variant="neutral-secondary"
              icon={<FeatherUpload />}
              onClick={() => setBulkUploadOpen(true)}
              disabled={permissionsLoading}
            >
              Bulk upload
            </Button>
          ) : null}
          <Button
            icon={<FeatherPlus />}
            onClick={() => {
              navigate("/add-customer", {
                state: { returnTo: "masters", mastersTab: "customers" },
              });
            }}
          >
            Add Customer
          </Button>
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        <Table
          header={
            <Table.HeaderRow>
              <Table.HeaderCell>Name</Table.HeaderCell>
              <Table.HeaderCell>Country</Table.HeaderCell>
              <Table.HeaderCell>State</Table.HeaderCell>
              <Table.HeaderCell>GSTIN</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.HeaderRow>
          }
        >
          {showTableSkeleton ? (
            <TableSkeletonRows columnCount={5} rowCount={10} />
          ) : showInlineLoading ? (
            <Table.Row>
              <Table.Cell colSpan={5}>
                <div className="flex w-full items-center justify-center py-10">
                  <Loader />
                </div>
              </Table.Cell>
            </Table.Row>
          ) : items.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={5}>
                <div className="flex w-full items-center justify-center py-10">
                  <span className="text-body font-body text-subtext-color">
                    {search.trim()
                      ? "No customers match your search"
                      : "No customers found"}
                  </span>
                </div>
              </Table.Cell>
            </Table.Row>
          ) : (
            items.map((c) => {
              const name = (c.customer_name ?? "").trim();
              const gstin = (c.gstin_vat_tax_id ?? "").trim();
              const isUnderReview = Number(c.is_under_review ?? 0) === 1;
              return (
                <Table.Row key={c.customer_id ?? c.id ?? name}>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <span className="text-body-bold font-body-bold text-default-font">
                        {name || "—"}
                      </span>
                      {isUnderReview && (
                        <Badge variant="warning">Under Review</Badge>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {(c.country ?? "").trim() || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {(c.state ?? "").trim() || "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {gstin ? gstin : "No GSTIN"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-wrap items-center gap-1">
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherEdit />}
                        disabled={isUnderReview}
                        title={
                          isUnderReview
                            ? "This customer is under review"
                            : undefined
                        }
                        onClick={() => {
                          if (isUnderReview) return;
                          navigate("/edit-customer", {
                            state: {
                              customerId: c.zoho_customerid || c.customer_id || String(c.id ?? ""),
                              customerIdInt: c.customer_id,
                              returnTo: "masters",
                              mastersTab: "customers",
                            },
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="neutral-tertiary"
                        size="small"
                        icon={<FeatherTrash />}
                        disabled={
                          permissionsLoading || !canDelete || isUnderReview
                        }
                        title={
                          isUnderReview
                            ? "This customer is under review"
                            : undefined
                        }
                        onClick={() => void handleDeleteClick(c)}
                      >
                        Delete
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })
          )}
        </Table>
      </div>

      {!showTableSkeleton &&
        !showInlineLoading &&
        pagination &&
        pagination.total > (pagination.limit || DEFAULT_LIMIT) && (
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Showing {Math.min(offset + 1, pagination.total)} –{" "}
              {Math.min(
                offset + (pagination.limit || DEFAULT_LIMIT),
                pagination.total,
              )}{" "}
              of {pagination.total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="neutral-tertiary"
                size="small"
                icon={<FeatherChevronLeft />}
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-body font-body text-default-font px-2">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="neutral-tertiary"
                size="small"
                icon={<FeatherChevronRight />}
                onClick={() => handlePageChange(page + 1)}
                disabled={!pagination.has_more}
              >
                Next
              </Button>
            </div>
          </div>
        )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && customerToDelete && (
        <DialogLayout
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          className="min-w-[460px] max-w-[520px]"
        >
          <div className="flex w-full flex-col">
            {/* Header */}
            <div className="flex w-full items-start justify-between gap-4 border-b border-solid border-neutral-border p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-100">
                  <FeatherAlertTriangle className="h-5 w-5 text-error-600" />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Delete customer
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    This will permanently remove{" "}
                    <strong>{customerToDelete.name}</strong> from Zoho Books.
                  </span>
                </div>
              </div>
              <IconButton
                size="small"
                icon={<FeatherX />}
                onClick={() => {
                  if (isDeleting) return;
                  setDeleteDialogOpen(false);
                  setCustomerToDelete(null);
                }}
                disabled={isDeleting}
              />
            </div>

            {/* Content */}
            <div className="flex w-full flex-col gap-5 p-6">
              <div className="rounded-md bg-error-50 p-4">
                <span className="text-body font-body text-default-font">
                  This action cannot be undone.
                </span>
              </div>

              <div className="flex w-full items-center justify-end gap-3">
                <Button
                  variant="neutral-secondary"
                  onClick={() => {
                    if (isDeleting) return;
                    setDeleteDialogOpen(false);
                    setCustomerToDelete(null);
                  }}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive-primary"
                  onClick={() => void handleDeleteConfirm()}
                  loading={isDeleting}
                  disabled={isDeleting}
                >
                  {deleteRequiresReview ? "Submit for Review" : "Delete"}
                </Button>
              </div>
            </div>
          </div>
        </DialogLayout>
      )}

      <BulkUploadModal
        open={bulkUploadOpen}
        onOpenChange={setBulkUploadOpen}
        kind="customers"
        requiresReview={bulkUploadPerm.requiresReview}
        moduleCode={MODULE_CODES.CUSTOMER}
        entityType="customer"
        onUploadComplete={() => void fetchCustomers()}
      />

      {reviewTarget && (
        <ReviewRequestModal
          open={reviewModalOpen}
          onOpenChange={(open) => {
            setReviewModalOpen(open);
            if (!open) setReviewTarget(null);
          }}
          moduleCode={MODULE_CODES.CUSTOMER}
          actionCode={ACTION_CODES.DELETE}
          entityType="customer"
          entityId={reviewTarget.entityId}
          documentNumber={reviewTarget.name}
          title="Request approval to delete customer"
          skipNavigation
          changeDetails={{
            action_types: [ACTION_CODES.DELETE],
            raw_json: {
              zoho_contact_id: reviewTarget.zohoContactId,
              zoho_customerid: reviewTarget.zohoContactId,
              customer_name: reviewTarget.name,
            },
          }}
          onSuccess={() => {
            setReviewModalOpen(false);
            setReviewTarget(null);
            void fetchCustomers();
          }}
        />
      )}
    </div>
  );
};
